import { useQuery, useQueryClient } from '@tanstack/react-query';
import { TabBar } from '@wot/components';
import { useEffect, useState } from 'react';
import studentsApi from '../../../api/students';
import LoadingContent from '../../../components/LoadingContent';
import NoResults from '../../../components/NoResults';
import Pagination from '../../../components/Pagination';
import { TabItem } from '../../../components/TabItem';
import usePagainate from '../../../hooks/usePaginate';
import { useTab } from '../../../hooks/useTab';
import TableContainer from './components/Table';

export type ISorting = 'createdAt';

export default function ManageStudents() {
  const queryClient = useQueryClient();
  const { currentPage, resultsPerPage, nextPage, prevPage, setResultsPerPage } = usePagainate();
  const { activeTab, onTabChange } = useTab('all');
  const [sortBy, setSortBy] = useState<{ [key: string]: number }>({ createdAt: -1 });

  const { data, isLoading, isError, isPreviousData } = useQuery({
    queryKey: ['students', currentPage, resultsPerPage, activeTab, sortBy],
    queryFn: async () =>
      await studentsApi.getAll(currentPage, resultsPerPage, activeTab === 'all' ? {} : { status: activeTab, sortBy }, {
        ...sortBy
      })
  });

  const handleSort = (key: ISorting) => {
    setSortBy({ [key]: sortBy[key] === 1 ? -1 : 1 });
  };

  useEffect(() => {
    if (!isPreviousData) {
      queryClient.prefetchQuery({
        queryKey: ['students', currentPage, resultsPerPage, activeTab, sortBy],
        queryFn: async () =>
          await studentsApi.getAll(
            currentPage,
            resultsPerPage,
            activeTab === 'all' ? {} : { status: activeTab, sortBy },
            { ...sortBy }
          )
      });
    }
  }, [data, isPreviousData, currentPage, resultsPerPage, queryClient, isError]);

  if (isError) {
    return (
      <div className="w-full min-h-[256px] flex items-center justify-center">
        <p className="font-medium">Something went Wrong.</p>
      </div>
    );
  }

  return (
    <div className="w-full flex flex-col gap-4 p-2 grow">
      <h1 className="text-xl font-semibold">Student Management</h1>
      <TabBar value={activeTab} onChange={onTabChange} textColor="text-black" indicatorColor="text-primary">
        <TabItem label="All" value="all" />
        <TabItem label="Active" value="active" />
        {/* <TabItem label="Inactive" value="inactive" /> */}
        <TabItem label="Blocked" value="block" />
      </TabBar>
      {isLoading ? (
        <LoadingContent />
      ) : data && data.data.length > 0 ? (
        <>
          <TableContainer data={data.data} handleSort={handleSort} />
          <Pagination
            count={data.count}
            currentPage={currentPage}
            nextPage={nextPage}
            prevPage={prevPage}
            resultsPerPage={resultsPerPage}
            setResultsPerPage={setResultsPerPage}
          />
        </>
      ) : (
        <NoResults />
      )}
    </div>
  );
}
